/**
 * ContingencyTimeline — the notices and events on one Wisconsin contingency
 * (design §5.3): what happened, who did it, and when, in Central time, with a
 * "View notice" link to each notice's PDF.
 *
 * Opening a notice you received records the first view (`POST …/viewed`).
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  downloadNoticeDocument,
  markNoticeViewed,
  noticeErrorInfo,
} from '../../../agents/contingencyNoticesAgent';
import { actorLabel, eventLabel, formatCentralInstant, onTimeLabel, parseApiInstant } from './contractFormat';
import styles from './ContingencyTimeline.module.css';

/** attention_set rows that duplicate a more specific flag event. */
const DUPLICATE_ATTENTION = ['cure_disputed', 'land_division_disputed', 'offplatform_notice', 'not_applicable_disputed'];

/**
 * The timeline rows for a contingency: its events (minus duplicates and
 * dry-run rows), plus any delivered notice the events don't mention yet.
 */
export function timelineEntries(contingencyId, contract, events = []) {
  const notices = contract?.notices || [];
  const byNoticeId = new Map(notices.map((n) => [n.id, n]));
  const rows = (events || [])
    .filter((e) => e.contingency_id === contingencyId && !e.dry_run)
    .filter((e) => !(e.event_type === 'attention_set' && DUPLICATE_ATTENTION.includes(e.reason)))
    .map((e) => ({
      key: `event-${e.id}`,
      at: e.at,
      label: eventLabel(e, contract, byNoticeId.get(e.notice_id)),
      actor: actorLabel(e.actor_type),
      notice: e.event_type === 'notice_delivered' ? (byNoticeId.get(e.notice_id) || { id: e.notice_id }) : null,
      attachment: e.event_type === 'offplatform_notice_reported' ? e.details?.attachment : null,
      kind: e.event_type,
    }));
  const covered = new Set(rows.filter((r) => r.notice?.id).map((r) => r.notice.id));
  notices.filter((n) => !covered.has(n.id)).forEach((n) => {
    rows.push({
      key: `notice-${n.id}`,
      at: n.delivered_at,
      label: eventLabel({ event_type: 'notice_delivered' }, contract, n),
      actor: actorLabel(n.actor_role),
      notice: n,
      attachment: null,
      kind: 'notice_delivered',
    });
  });
  return rows.sort((a, b) => String(a.at || '').localeCompare(String(b.at || '')));
}

function ViewNoticeButton({ notice, role }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const viewedRef = useRef(false);

  const open = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await downloadNoticeDocument(notice.id);
      const isRecipient = notice.actor_role && role && notice.actor_role !== role;
      if (isRecipient && !viewedRef.current) {
        viewedRef.current = true;
        markNoticeViewed(notice.id).catch(() => { viewedRef.current = false; });
      }
    } catch (err) {
      setError(noticeErrorInfo(err).message);
    } finally {
      setLoading(false);
    }
  }, [notice.id, notice.actor_role, role]);

  return (
    <span className={styles.viewWrap}>
      <button
        type="button"
        className={styles.viewBtn}
        onClick={open}
        disabled={loading}
        data-testid={`view-notice-${notice.id}`}
      >
        {loading ? 'Opening…' : 'View notice'}
      </button>
      {error && <span className={styles.inlineError} role="alert">{error}</span>}
    </span>
  );
}

export default function ContingencyTimeline({ contingencyId, contract, events, loading, error, onRetry, role }) {
  const rows = useMemo(() => timelineEntries(contingencyId, contract, events), [contingencyId, contract, events]);
  const headingId = `timeline-heading-${contingencyId}`;

  return (
    <section className={styles.timeline} aria-labelledby={headingId} data-testid={`timeline-${contingencyId}`}>
      <h4 id={headingId} className={styles.heading}>Timeline</h4>
      {loading && rows.length === 0 && <p className={styles.muted} role="status">Loading the timeline…</p>}
      {error && (
        <p className={styles.error} role="alert">
          {error}{' '}
          {onRetry && <button type="button" className={styles.retry} onClick={onRetry}>Try again</button>}
        </p>
      )}
      {!loading && !error && rows.length === 0 && (
        <p className={styles.muted}>Nothing recorded yet.</p>
      )}
      {rows.length > 0 && (
        <ol className={styles.list}>
          {rows.map((row) => (
            <li key={row.key} className={`${styles.item} ${row.notice ? styles.isNotice : ''}`}>
              <span className={styles.dot} aria-hidden="true" />
              <div className={styles.body}>
                <p className={styles.label}>{row.label}</p>
                <p className={styles.meta}>
                  {row.actor && <span className={styles.actor}>{row.actor}</span>}
                  {row.at && <time dateTime={parseApiInstant(row.at)?.toISOString()}>{formatCentralInstant(row.at)}</time>}
                </p>
                {row.notice && row.notice.on_time !== undefined && (
                  <p className={styles.onTime}>{onTimeLabel(row.notice.on_time)}</p>
                )}
                {row.notice?.id && <ViewNoticeButton notice={row.notice} role={role} />}
                {row.attachment?.filename && (
                  <p className={styles.meta}>{`Copy on file: ${row.attachment.filename}`}</p>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
